import { useStudyText } from '@/i18n/studyText';
import { workflowTitles, type WorkflowModule } from '../workflowApi';
import { useAdvisorText } from '@/features/advisor/labels';
import { DoctoralCoordinationPanel } from './DoctoralCoordinationPanel';
import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import { useI18n } from '@/hooks/useI18n';

export function DoctoralGroupLeaderPage({ onClose }: { onClose: () => void }) {
  const { locale } = useI18n();
  const st = useStudyText();
  const statusText = useAdvisorText();
  const text = (ru: string, kz: string, en: string) =>
    locale === 'kz' ? kz : locale === 'en' ? en : ru;
  const [assignment, setAssignment] = useState<{ group: string; start: string; end: string }>();
  const [records, setRecords] = useState<
    {
      student_id: string;
      name: string;
      percent: number | null;
      overdue?: number;
      updated_at?: string | null;
      modules: { module: string; percent: number | null; review_status: string }[];
    }[]
  >([]);
  const [rotation, setRotation] = useState<
    { student_id: string; name: string; assigned: number; completed: number }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      apiRequest<{ group: string; start: string; end: string }>('/doctoral/group-leader', {
        channel: 'platform',
      })
        .then((data) => {
          if (!cancelled) {
            setAssignment(data);
            setFailed(false);
          }
          return Promise.all([
            apiRequest<{ records: typeof records }>('/doctoral/group-leader/stats', {
              channel: 'platform',
            }),
            apiRequest<typeof rotation>('/doctoral/group-leader/rotation', { channel: 'platform' }),
          ]).then(([stats, rotation]) => {
            if (!cancelled) {
              setRecords(stats.records || []);
              setRotation(Array.isArray(rotation) ? rotation : []);
            }
          });
        })
        .catch(() => {
          if (!cancelled) {
            setFailed(true);
            setAssignment(undefined);
            setRecords([]);
            setRotation([]);
          }
        });
    void refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, 15000);
    return () => {
      window.clearInterval(timer);
      cancelled = true;
    };
  }, []);
  return (
    <section className="p-6">
      <button onClick={onClose}>{text('Назад', 'Артқа', 'Back')}</button>
      <h2>
        {text('Полномочия старосты', 'Старостаның өкілеттіктері', 'Group leader responsibilities')}
      </h2>
      {failed ? (
        <p role="alert">
          {text(
            'Доступ к полномочиям старосты недоступен.',
            'Староста өкілеттіктеріне қол жеткізу мүмкін емес.',
            'Group leader access is unavailable.',
          )}
        </p>
      ) : assignment ? (
        <>
          <p>
            {text('Группа', 'Топ', 'Group')}: {assignment.group}
          </p>
          <p>
            {text('Период', 'Кезең', 'Period')}: {assignment.start} — {assignment.end}
          </p>
          {message && <p role="status">{message}</p>}
          <table>
            <thead>
              <tr>
                <th>{text('Докторант', 'Докторант', 'Student')}</th>
                <th>{text('Готовность', 'Дайындық', 'Readiness')}</th>
                <th>
                  {text(
                    'Просрочено ознакомление',
                    'Танысу мерзімі өтті',
                    'Overdue acknowledgements',
                  )}
                </th>
                <th>{text('Обновлено', 'Жаңартылды', 'Updated')}</th>
                <th>{text('Разделы', 'Бөлімдер', 'Sections')}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.student_id}>
                  <td>{record.name}</td>
                  <td>{record.percent === null ? '—' : `${record.percent}%`}</td>
                  <td>{record.overdue ?? 0}</td>
                  <td>
                    {record.updated_at
                      ? new Date(record.updated_at).toLocaleString(
                          locale === 'kz' ? 'kk-KZ' : locale === 'en' ? 'en-GB' : 'ru-RU',
                        )
                      : '—'}
                  </td>
                  <td>
                    {record.modules.map((module) => (
                      <div key={module.module}>
                        <span>
                          {st(workflowTitles[module.module as WorkflowModule] || module.module)} ·{' '}
                          {module.percent === null ? '—' : `${module.percent}%`} ·{' '}
                          {statusText(module.review_status)}
                        </span>
                        <button
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await apiRequest('/doctoral/group-leader/reminders', {
                                channel: 'platform',
                                method: 'POST',
                                body: {
                                  student_id: record.student_id,
                                  module: module.module,
                                  request_id: crypto.randomUUID(),
                                },
                              });
                              setMessage(
                                text(
                                  'Напоминание отправлено',
                                  'Еске салу жіберілді',
                                  'Reminder sent',
                                ),
                              );
                            } catch {
                              setMessage(
                                text(
                                  'Не удалось отправить напоминание',
                                  'Еске салуды жіберу мүмкін болмады',
                                  'Unable to send reminder',
                                ),
                              );
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          {text('Напомнить', 'Еске салу', 'Remind')}
                        </button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!records.length && (
            <p>
              {text(
                'В группе нет активных докторантов',
                'Топта белсенді докторанттар жоқ',
                'No active doctoral students in this group',
              )}
            </p>
          )}
          <h3>{text('Очередность дежурств', 'Кезекшілік реті', 'Duty rotation')}</h3>
          <table>
            <thead>
              <tr>
                <th>{text('Докторант', 'Докторант', 'Student')}</th>
                <th>{text('Назначено', 'Тағайындалған', 'Assigned')}</th>
                <th>{text('Выполнено', 'Орындалған', 'Completed')}</th>
              </tr>
            </thead>
            <tbody>
              {rotation.map((r) => (
                <tr key={r.student_id}>
                  <td>{r.name}</td>
                  <td>{r.assigned}</td>
                  <td>{r.completed}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <DoctoralCoordinationPanel kind="duties" onClose={onClose} />
        </>
      ) : (
        <p>{text('Загрузка…', 'Жүктелуде…', 'Loading…')}</p>
      )}
    </section>
  );
}
